import { API } from "@infra/implementations/api";
import { FileDownloader } from "@infra/implementations/browser/download";
import { logger } from "@infra/implementations/logger";
import type { IJobCreation } from "@infra/interfaces/jobs/IJobCreation";
import type { IAttachmentDownloader } from "@infra/interfaces/browser/IAttachmentDownloader";
import type { IJobs } from "@infra/interfaces/jobs/IJobs";
import type { Branch } from "@domain/branch";
import type { Contact } from "@domain/contact";
import type { Deal } from "@domain/deal";
import type { User } from "@domain/user";
import type { Grower } from "@domain/grower";
import type { Job } from "@domain/job";
import { SubmitJobsUseCase } from "@app/jobs/SubmitJobsUseCase";
import { MissionsUseCase } from "@app/jobs/MissionsUseCase";
import { JobsListUseCase, JobsDetailUseCase, MarkJobsReadyUseCase } from "@app/jobs";

const jobCreationApi: IJobCreation = {
	getGrowersByFilter: (filter) => API.Data.Growers.get.byFilter(filter) as Promise<Grower[]>,
	createGrowers: (growers) => API.Data.Growers.create.many(growers) as Promise<Grower[]>,
	createContacts: (contacts) => API.Data.Contacts.create.many(contacts) as Promise<Contact[]>,
	getContactsByIds: (ids) => API.Data.Contacts.get.byIds(ids) as Promise<Contact[]>,
	getBranchesByIds: (ids) => API.Data.Branches.get.byIds(ids) as Promise<Branch[]>,
	updateContacts: (contacts) => API.Data.Contacts.update.many(contacts) as unknown as Promise<void>,
	updateBranches: (branches) => API.Data.Branches.update.many(branches) as unknown as Promise<void>,
};

const attachmentDownloader: IAttachmentDownloader = {
	load: (attachment, recordId, responseType) => FileDownloader.loadAttachment(attachment, recordId, responseType),
};

const jobsApi: IJobs = {
	getById: (id, useCache) => API.Data.Jobs.get.byId(id, useCache) as Promise<Job | undefined>,
	getByFilter: (filter) => API.Data.Jobs.get.byFilter(filter) as Promise<Job[]>,
	getLastCropOptions: () => API.Data.Jobs.get.lastCropOptions(),
	getNextCropOptions: () => API.Data.Jobs.get.nextCropOptions(),
	updateOne: (job) => API.Data.Jobs.update.one(job as Job) as Promise<Job>,
};

export const submitJobsUseCase = new SubmitJobsUseCase({
	jobCreationApi,
	getCurrentUser: () => API.Users.me() as Promise<User>,
	getDealsByIds: (ids) => API.Data.Deals.get.byIds(ids) as Promise<Deal[]>,
	createJobs: (jobs) => API.Data.Jobs.create.many(jobs) as Promise<Job[]>,
	logger,
});

export const missionsUseCase = new MissionsUseCase({ downloader: attachmentDownloader });
export const jobsListUseCase = new JobsListUseCase({ jobsApi });
export const jobsDetailUseCase = new JobsDetailUseCase({ jobsApi });
export const markJobsReadyUseCase = new MarkJobsReadyUseCase({
	createJobsReady: (form) => API.Data.JobsReady.create.one(form) as Promise<any>,
	updateJobs: (jobs, compressed) => API.Data.Jobs.update.many(jobs, compressed) as unknown as Promise<void>,
	logger,
});
